import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { AlertCircleIcon, Trash2Icon, PlusIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Badge,
	Button,
	DatePicker,
	Form, FormControl, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	IconButton, Input, SelectField,
	TextField,
} from '@rentflowdev/ui'
import { useUpsertCompanyProfileMutation } from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { extractErrorMessage } from '@/lib/error-message'
import { accountTypeLabel, signatoryBasisLabel } from '@/lib/account-labels'
import type { AccountType, CompanyProfile, Signatory, SignatoryBasis, User } from '@/types'
import { SIGNATORY_BASIS } from '@/types'

const signatoryBasisEnum = z.enum(SIGNATORY_BASIS)

const signatorySchema = z.object({
	id:              z.string().optional(),
	fullName:        z.string().trim().optional(),
	position:        z.string().trim().optional(),
	basis:           signatoryBasisEnum.optional(),
	proxyNumber:     z.string().trim().optional(),
	proxyValidFrom:  z.string().optional(),
	proxyValidUntil: z.string().optional(),
})

const editSchema = z.object({
	// Информация об арендодателе
	fullName:         z.string().trim().optional(),
	inn:              z.string().trim().optional(),
	ogrn:             z.string().trim().optional(),
	kpp:              z.string().trim().optional(),
	companyName:      z.string().trim().optional(),
	registrationAddr: z.string().trim().optional(),
	address:          z.string().trim().optional(),
	passportSeries:   z.string().trim().optional(),
	passportNumber:   z.string().trim().optional(),
	passportIssuedBy: z.string().trim().optional(),

	// Банковские реквизиты
	bank:        z.string().trim().optional(),
	bankAccount: z.string().trim().optional(),
	corrAccount: z.string().trim().optional(),
	bik:         z.string().trim().optional(),

	// Подписанты (первый — всегда self)
	signatories: z.array(signatorySchema).optional(),
}).superRefine((values, ctx) => {
	// Messages are i18n keys, translated by `zodResolverI18n` before they
	// reach the form, so plain `FormMessage` prints them.
	values.signatories?.forEach((s, index) => {
		// Подписант нулевой — это вы, и его имя приходит из профиля, поэтому поле
		// только для чтения. Но колонка `fullName` в базе обязательна, а пустое
		// имя в профиле записывалось туда пустой строкой: требуем и здесь, иначе
		// пустоту нечем заметить.
		if (!s.fullName?.trim()) {
			ctx.addIssue({
				code:    z.ZodIssueCode.custom,
				path:    ['signatories', index, 'fullName'],
				message: 'settings.profile.contract.validation.signatoryNameRequired',
			})
		}
		if (s.basis === 'POWER_OF_ATTORNEY') {
			if (!s.proxyNumber?.trim()) {
				ctx.addIssue({
					code:    z.ZodIssueCode.custom,
					path:    ['signatories', index, 'proxyNumber'],
					message: 'settings.profile.contract.validation.proxyNumberRequired',
				})
			}
			if (!s.proxyValidFrom) {
				ctx.addIssue({
					code:    z.ZodIssueCode.custom,
					path:    ['signatories', index, 'proxyValidFrom'],
					message: 'settings.profile.contract.validation.proxyValidFromRequired',
				})
			}
			if (!s.proxyValidUntil) {
				ctx.addIssue({
					code:    z.ZodIssueCode.custom,
					path:    ['signatories', index, 'proxyValidUntil'],
					message: 'settings.profile.contract.validation.proxyValidUntilRequired',
				})
			}
		}
	})
})

type EditContractFormValues = z.infer<typeof editSchema>

type ProxyDatePath = `signatories.${number}.proxyValidFrom` | `signatories.${number}.proxyValidUntil`

/** `DateField` clone that renders its zod message (an i18n key) translated. */
function ProxyDateField({ name }: { name: ProxyDatePath }) {
	const form = useFormContext<EditContractFormValues>()
	return (
		<FormField
			control={form.control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormControl>
						<DatePicker
							value={field.value ? new Date(field.value) : undefined}
							onChange={(d) => field.onChange(d ? d.toISOString().slice(0, 10) : '')}
							name={name}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

interface EditContractDataDialogProps {
	accountType:  AccountType
	accountId:    string
	profile?:     CompanyProfile
	me?:          User
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function EditContractDataDialog({
	accountType, accountId, profile, me, open, onOpenChange,
}: EditContractDataDialogProps) {
	const { t } = useTranslation()
	const [upsertProfile, { isLoading }] = useUpsertCompanyProfileMutation()
	const [hasError, setHasError] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	// The parent keeps this dialog mounted, so error state would otherwise
	// survive close/reopen — clear it whenever the dialog opens.
	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasError(false)
			setErrorMsg(undefined)
		}
	}, [open])

	const defaultSignatories: Signatory[] = (profile?.signatories && profile.signatories.length > 0)
		? profile.signatories
		: [{ fullName: me?.name ?? '', basis: profile?.signatoryBasis ?? 'PERSONAL', isSelf: true }]

	const { form, resetDraft } = usePersistedForm<EditContractFormValues>({
		key:           `contract:edit:${accountId}`,
		recordVersion: profile?.updatedAt,
		defaultValues: {
			fullName:         profile?.name             ?? me?.name ?? '',
			inn:              profile?.inn              ?? '',
			ogrn:             profile?.ogrn             ?? '',
			kpp:              profile?.kpp              ?? '',
			companyName:      profile?.name             ?? '',
			registrationAddr: profile?.registrationAddr ?? '',
			address:          profile?.address          ?? '',
			passportSeries:   profile?.passportSeries   ?? '',
			passportNumber:   profile?.passportNumber   ?? '',
			passportIssuedBy: profile?.passportIssuedBy ?? '',
			bank:             profile?.bank             ?? '',
			bankAccount:      profile?.bankAccount      ?? '',
			corrAccount:      profile?.corrAccount      ?? '',
			bik:              profile?.bik              ?? '',
			signatories:      defaultSignatories,
		},
		resolver: zodResolverI18n(editSchema),
		mode:     'onBlur',
		active:   open,
	})

	const { fields, append, remove } = useFieldArray({ control: form.control, name: 'signatories' })

	async function onSubmit(values: EditContractFormValues) {
		setHasError(false)
		setErrorMsg(undefined)
		try {
			await upsertProfile({
				// Название организации есть только у компании, и только там оно
				// вправе побеждать. У физлица поле не показывают, но оно хранит
				// загруженное значение — и правка ФИО не сохранялась ни разу.
				name:             (isCompany ? values.companyName : values.fullName) || undefined,
				signatory:        values.fullName          || undefined,
				inn:              values.inn              || undefined,
				ogrn:             values.ogrn             || undefined,
				kpp:              values.kpp              || undefined,
				address:          values.address          || undefined,
				registrationAddr: values.registrationAddr || undefined,
				passportSeries:   values.passportSeries   || undefined,
				passportNumber:   values.passportNumber   || undefined,
				passportIssuedBy: values.passportIssuedBy || undefined,
				bank:             values.bank             || undefined,
				bankAccount:      values.bankAccount      || undefined,
				corrAccount:      values.corrAccount      || undefined,
				bik:              values.bik              || undefined,
				signatories: values.signatories?.map((s) => ({
					id:              s.id,
					fullName:        s.fullName?.trim() ?? '',
					position:        s.position || undefined,
					basis:           s.basis ?? 'PERSONAL',
					proxyNumber:     s.proxyNumber     || undefined,
					proxyValidFrom:  s.proxyValidFrom  || undefined,
					proxyValidUntil: s.proxyValidUntil || undefined,
				})),
			}).unwrap()
			resetDraft()
			onOpenChange(false)
		} catch (err) {
			setHasError(true)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	const basisOptions: { value: SignatoryBasis; label: string }[] = SIGNATORY_BASIS.map((value) => ({
		value,
		label: signatoryBasisLabel(t, value) ?? value,
	}))

	const typeLabel = accountTypeLabel(t, accountType)

	const isIndividual = accountType === 'INDIVIDUAL'
	const isCompany    = accountType === 'COMPANY'

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={
					<span className="inline-flex items-center gap-2">
						{t('settings.profile.subtabs.contract')}
						<Badge variant="info">{typeLabel}</Badge>
					</span>
				}
				onSubmit={form.handleSubmit(onSubmit)}
				isSubmitting={isLoading}
				submitLabel={t('common.save', { defaultValue: 'Сохранить' })}
				cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
				contentClassName="sm:max-w-2xl"
			>
				{hasError && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{t('settings.profile.contract.errors.save')}</AlertTitle>
						{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
					</Alert>
				)}

				{/* Landlord information */}
				<section className="flex flex-col gap-3">
					<h3 className="text-base font-[500]">{t('settings.profile.contract.landlordInfo')}</h3>
					<div className="grid grid-cols-2 gap-3">
						<TextField name="fullName" label={t('settings.profile.contract.fullName')} />
						{isCompany && <TextField name="companyName" label={t('settings.profile.contract.companyName')} />}
						<TextField name={isIndividual ? 'registrationAddr' : 'address'} label={
							isIndividual
								? t('settings.profile.contract.registrationAddr')
								: t('properties.create.legalAddress', { defaultValue: 'Юридический адрес' })
						} />
						<TextField name="inn" label={t('settings.profile.contract.inn')} />
						{!isIndividual && (
							<TextField
								name="ogrn"
								label={accountType === 'ENTREPRENEUR'
									? t('settings.profile.contract.ogrnIp')
									: t('settings.profile.contract.ogrn')}
							/>
						)}
						{isCompany && <TextField name="kpp" label={t('settings.profile.contract.kpp')} />}
					</div>
					<button type="button" className="self-start text-sm text-(--color-brand) hover:underline" disabled>
						{t('settings.profile.contract.fillFromGosuslugi')}
					</button>

					{isIndividual && (
						<>
							<FormLabel className="mt-1">{t('settings.profile.contract.passportSeriesNumber')}</FormLabel>
							<div className="grid grid-cols-2 gap-3">
								<FormField
									control={form.control}
									name="passportSeries"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input {...field} value={field.value ?? ''} placeholder="серия  1234" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="passportNumber"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<Input {...field} value={field.value ?? ''} placeholder="№  4556788" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<TextField name="passportIssuedBy" label={t('settings.profile.contract.passportIssuedBy')} />
						</>
					)}
				</section>

				{/* Банковские реквизиты */}
				<section className="flex flex-col gap-3 mt-5">
					<h3 className="text-base font-[500]">{t('settings.profile.contract.bankRequisites')}</h3>
					<div className="grid grid-cols-2 gap-3">
						<TextField name="bank"        label={t('settings.profile.contract.bank')} />
						<TextField name="bankAccount" label={t('settings.profile.contract.checkingAccount')} />
						<TextField name="corrAccount" label={t('settings.profile.contract.corrAccount')} />
						<TextField name="bik"         label={t('settings.profile.contract.bik')} />
					</div>
				</section>

				{/* Подписанты */}
				<section className="flex flex-col gap-4 mt-5">
					{fields.map((field, index) => {
						const isSelf = index === 0
						const basis = form.watch(`signatories.${index}.basis`)
						return (
							<div key={field.id} className="flex flex-col gap-3">
								<div className="flex items-center gap-2">
									<h3 className="text-base font-[500]">
										{t('settings.profile.contract.signatoryNumber', { n: index + 1 })}
									</h3>
									{!isSelf && (
										<IconButton
											type="button"
											variant="ghost"
											size="sm"
											aria-label={t('settings.profile.contract.removeSignatory')}
											onClick={() => remove(index)}
										>
											<Trash2Icon className="size-4 text-(--color-muted-foreground)" />
										</IconButton>
									)}
								</div>
								<div className="grid grid-cols-2 gap-3">
									{isSelf ? (
										<FormItem>
											<FormLabel>{t('settings.profile.contract.fullName')}</FormLabel>
											<FormControl>
												<Input
													value={me?.name ? `${me.name} (${t('settings.profile.contract.selfMarker')})` : ''}
													readOnly
													disabled
												/>
											</FormControl>
										</FormItem>
									) : (
										<FormField
											control={form.control}
											name={`signatories.${index}.fullName`}
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('settings.profile.contract.fullName')}</FormLabel>
													<FormControl>
														<Input {...field} value={field.value ?? ''} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}
									<SelectField
										name={`signatories.${index}.basis`}
										label={t('settings.profile.contract.basis')}
										options={basisOptions}
									/>
									{!isSelf && (
										<TextField name={`signatories.${index}.position`} label={t('settings.profile.contract.position')} />
									)}
									{basis === 'POWER_OF_ATTORNEY' && (
										<FormField
											control={form.control}
											name={`signatories.${index}.proxyNumber`}
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('settings.profile.contract.proxyNumber')}</FormLabel>
													<FormControl>
														<Input {...field} value={field.value ?? ''} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}
								</div>
								{basis === 'POWER_OF_ATTORNEY' && (
									<>
										<FormLabel>{t('settings.profile.contract.proxyValidity')}</FormLabel>
										<div className="grid grid-cols-2 gap-3">
											<ProxyDateField name={`signatories.${index}.proxyValidFrom`} />
											<ProxyDateField name={`signatories.${index}.proxyValidUntil`} />
										</div>
									</>
								)}
							</div>
						)
					})}
					<Button
						type="button"
						variant="secondary"
						onClick={() => append({ fullName: '', basis: 'POWER_OF_ATTORNEY' })}
					>
						<PlusIcon className="mr-1 size-4" />
						{t('settings.profile.contract.addSignatory')}
					</Button>
				</section>
			</FormDialog>
		</Form>
	)
}
